import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  IconChartBar,
  IconDashboard,
  IconDatabase,
  IconFileDescription,
  IconSettings,
  IconUsers,
  IconBasketDiscount,
  IconCategoryPlus,
} from '@tabler/icons-react';
import { NavMain } from '@/components/nav-main';
import { NavUser } from '@/components/nav-user';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import type { NavItem } from '@/types/routes';
import { NavSystemManagement } from './nav-system-management';
import { useTheme } from '@/components/theme-provider';

const getNavItems = (): NavItem[] => [
  {
    title: 'Dashboard',
    url: '/',
    icon: IconDashboard,
  },
  {
    title: 'Venues',
    url: '/venues',
    icon: IconDatabase,
    permission: 'venue_management:read',
  },
  {
    title: 'Offers',
    url: '/offers',
    icon: IconFileDescription,
    permission: 'offer_management:read',
  },
  {
    title: 'Promo Offers',
    url: '/promo-offers',
    icon: IconBasketDiscount,
    permission: 'offer_management:read',
  },
];

const getSystemManagementItems = (): NavItem[] => [
  {
    title: 'Admin Users',
    url: '/admin-users',
    icon: IconUsers,
    permission: 'system_management:read',
  },
  {
    title: 'Organizations',
    url: '/organizations',
    icon: IconCategoryPlus,
    permission: 'system_management:read',
  },
  {
    title: 'Settings',
    url: '/settings',
    icon: IconSettings,
    permission: 'system_management:read',
  },
  {
    title: 'Analytics',
    url: '/analytics',
    icon: IconChartBar,
    permission: 'system_management:read',
  },
];

// Unused - keeping for potential future use
/*
const staticData = {
  navClouds: [
    {
      title: 'Capture',
      icon: IconCamera,
      isActive: true,
      url: '#',
      items: [
        {
          title: 'Active Proposals',
          url: '#',
        },
        {
          title: 'Archived',
          url: '#',
        },
      ],
    },
    {
      title: 'Proposal',
      icon: IconFileDescription,
      url: '#',
      items: [
        {
          title: 'Active Proposals',
          url: '#',
        },
        {
          title: 'Archived',
          url: '#',
        },
      ],
    },
    {
      title: 'Prompts',
      icon: IconFileAi,
      url: '#',
      items: [
        {
          title: 'Active Proposals',
          url: '#',
        },
        {
          title: 'Archived',
          url: '#',
        },
      ],
    },
  ],
  navSecondary: [
    {
      title: 'Settings',
      url: '#',
      icon: IconSettings,
    },
    {
      title: 'Get Help',
      url: '#',
      icon: IconHelp,
    },
    {
      title: 'Search',
      url: '#',
      icon: IconSearch,
    },
  ],
  documents: [
    {
      name: 'Data Library',
      url: '#',
      icon: IconDatabase,
    },
    {
      name: 'Reports',
      url: '#',
      icon: IconReport,
    },
    {
      name: 'Word Assistant',
      url: '#',
      icon: IconFileWord,
    },
  ],
};
*/

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();

  // Get user-specific data
  const getUserData = () => {
    if (!user) {
      return {
        full_name: 'Loading...',
        phone_number: 'Loading...',
        avatar: '/avatars/default.jpg',
        role: 'Loading...',
      };
    }

    return {
      full_name:
        user.full_name ||
        `${user.role[0].replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} User`,
      phone_number: user.phone_number || 'N/A',
      avatar: `/avatars/${user.role[0]}.jpg`,
      role: user.role[0]
        .replace('_', ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase()),
    };
  };

  const userData = getUserData();

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-1.5 group-data-[collapsible=icon]:!p-2"
            >
              <Link to="/">
                <img
                  src={
                    resolvedTheme === 'dark' ? '/logo.png' : '/logo-dark.png'
                  }
                  alt="Urban Finch"
                  className="h-6 w-auto group-data-[collapsible=icon]:hidden"
                />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={getNavItems()} />
        <NavSystemManagement items={getSystemManagementItems()} />
        {/*<NavSecondary items={staticData.navSecondary} className="mt-auto" />*/}
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={userData} />
      </SidebarFooter>
    </Sidebar>
  );
}
